import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { leadsApi, teamApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PlusCircle, Phone, Search, Sparkles, MapPin, Calendar, Loader2, UserCheck, UserX, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const statusColor = {
  'Tele Caller': 'bg-sky-100 text-sky-800',
  'Follow-ups': 'bg-cyan-100 text-cyan-800',
  'Admin (Qualify)': 'bg-yellow-100 text-yellow-800',
  'Sales Manager': 'bg-indigo-100 text-indigo-800',
  'Design': 'bg-violet-100 text-violet-800',
  'Proposal Shared': 'bg-purple-100 text-purple-800',
  'Post Proposal Follow-up': 'bg-pink-100 text-pink-800',
  'Deal Won': 'bg-emerald-100 text-emerald-800',
  'Accounts': 'bg-amber-100 text-amber-800',
  'Installation Head - BOS': 'bg-orange-100 text-orange-800',
  'Admin (Procurement)': 'bg-rose-100 text-rose-800',
  'Sales Manager 80%': 'bg-fuchsia-100 text-fuchsia-800',
  'Installation': 'bg-lime-100 text-lime-800',
  'BESCOM Team': 'bg-teal-100 text-teal-800',
  'Handover & Complete': 'bg-green-200 text-green-900',
  'Lost / Dropped': 'bg-slate-200 text-slate-700',
};

export default function Leads() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'Director' || user.role === 'Admin');
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState('all');
  const [assignFilter, setAssignFilter] = useState('all'); // all | mine | unassigned
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: 'Bangalore', segment: 'Residential', kw: 5, source: 'Walk-in' });

  const load = async () => {
    setLoading(true);
    try {
      const [leadsData, teamData] = await Promise.all([leadsApi.list(), teamApi.list()]);
      setLeads(leadsData);
      setTeam(teamData);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const teamById = useMemo(() => Object.fromEntries(team.map(t => [t.id, t])), [team]);

  const changeAssignee = async (leadId, newAssigneeId) => {
    try {
      // Empty string = unassign
      const updated = await leadsApi.update(leadId, { assigned_to: newAssigneeId || '' });
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
      const nm = newAssigneeId ? (teamById[newAssigneeId]?.name || 'user') : 'Unassigned';
      toast.success(`Lead reassigned to ${nm}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassign failed');
    }
  };

  const filtered = useMemo(() => leads.filter(l =>
    (seg === 'all' || l.segment === seg) &&
    (assignFilter === 'all' ||
      (assignFilter === 'mine' && l.assigned_to === user?.id) ||
      (assignFilter === 'unassigned' && !l.assigned_to)) &&
    (q === '' || l.name.toLowerCase().includes(q.toLowerCase()) || l.city.toLowerCase().includes(q.toLowerCase()) || l.id.toLowerCase().includes(q.toLowerCase()))
  ), [leads, q, seg, assignFilter, user?.id]);

  const addLead = async () => {
    if (!form.name || !form.phone) { toast.error('Name and phone are required'); return; }
    setSaving(true);
    try {
      const created = await leadsApi.create({ ...form, kw: Number(form.kw) });
      setLeads(prev => [created, ...prev]);
      setOpen(false);
      setForm({ name: '', phone: '', email: '', city: 'Bangalore', segment: 'Residential', kw: 5, source: 'Walk-in' });
      toast.success(`Lead ${created.id} added successfully`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  const removeLead = async () => {
    if (!confirmDel) return;
    try {
      await leadsApi.remove(confirmDel.id);
      setLeads(prev => prev.filter(x => x.id !== confirmDel.id));
      toast.success(`Lead ${confirmDel.id} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setConfirmDel(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950">Leads</h1>
          <p className="text-slate-600 mt-1">{filtered.length} lead{filtered.length !== 1 ? 's' : ''} in your pipeline.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white"><PlusCircle className="w-4 h-4 mr-2" /> Add Lead</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader><DialogTitle className="text-emerald-950">Add new lead</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">Full Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Suresh Kumar" /></div>
                <div><Label className="mb-1.5 block">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 …" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
                <div>
                  <Label className="mb-1.5 block">Segment</Label>
                  <Select value={form.segment} onValueChange={v => setForm({...form, segment: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">Approx. System Size (kW)</Label><Input type="number" value={form.kw} onChange={e => setForm({...form, kw: e.target.value})} /></div>
                <div>
                  <Label className="mb-1.5 block">Source</Label>
                  <Select value={form.source} onValueChange={v => setForm({...form, source: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Walk-in','Website','Referral','JustDial','Google Ads','Facebook'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={addLead} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-emerald-100">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, city or lead ID…" className="pl-10" />
          </div>
          <Select value={seg} onValueChange={setSeg}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All segments</SelectItem>
              <SelectItem value="Residential">Residential</SelectItem>
              <SelectItem value="Commercial">Commercial</SelectItem>
              <SelectItem value="Industrial">Industrial</SelectItem>
            </SelectContent>
          </Select>
          {/* Assignment filter chips */}
          <div className="flex gap-1 items-center">
            {[
              { key: 'all', label: 'All' },
              { key: 'mine', label: 'My leads' },
              { key: 'unassigned', label: 'Unassigned' },
            ].map(chip => (
              <button
                key={chip.key}
                type="button"
                data-testid={`filter-${chip.key}`}
                onClick={() => setAssignFilter(chip.key)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${
                  assignFilter === chip.key
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                    : 'border-emerald-100 text-slate-600 hover:border-emerald-300'
                }`}
              >
                {chip.label}
                {chip.key === 'unassigned' && (
                  <span className="ml-1.5 text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full">
                    {leads.filter(l => !l.assigned_to).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(l => (
            <Card key={l.id} className="p-5 border-emerald-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white font-bold flex items-center justify-center">{l.name[0]}</div>
                  <div>
                    <div className="font-bold text-emerald-950">{l.name}</div>
                    <div className="text-xs text-slate-500">{l.id}</div>
                  </div>
                </div>
                <Badge className={`${statusColor[l.status] || 'bg-slate-100 text-slate-700'} hover:bg-inherit border-0 rounded-full text-[10px]`}>{l.status}</Badge>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Phone className="w-3.5 h-3.5" /> {l.phone}</div>
                <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-3.5 h-3.5" /> {l.city} • {l.segment}</div>
                <div className="flex items-center gap-2 text-slate-600"><Calendar className="w-3.5 h-3.5" /> Added {(l.created_at || '').slice(0,10)}</div>
              </div>
              {/* Assignee row */}
              <div className="mt-3 pt-3 border-t border-emerald-50">
                {isAdmin ? (
                  <div className="flex items-center gap-2">
                    {l.assigned_to
                      ? <UserCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                      : <UserX className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
                    <Select
                      value={l.assigned_to || '__unassigned__'}
                      onValueChange={(v) => changeAssignee(l.id, v === '__unassigned__' ? '' : v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`assign-${l.id}`}>
                        <SelectValue placeholder="Assign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__" data-testid={`assign-unassign-${l.id}`}>— Unassigned —</SelectItem>
                        {team.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    {l.assigned_to
                      ? <><UserCheck className="w-3.5 h-3.5 text-emerald-700" /><span className="text-emerald-800 font-semibold">{teamById[l.assigned_to]?.name || 'Assigned'}</span></>
                      : <><UserX className="w-3.5 h-3.5 text-rose-500" /><span className="text-rose-600 font-semibold">Unassigned</span></>}
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between pt-3 border-t border-emerald-50">
                <div>
                  <div className="text-xs text-slate-500">Approx. system</div>
                  <div className="text-lg font-extrabold text-emerald-900">{l.kw} <span className="text-sm font-medium text-slate-500">kW</span></div>
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-emerald-950 font-semibold" onClick={() => nav('/app/proposals/new', { state: { lead: l }})}>
                  <Sparkles className="w-4 h-4 mr-1" /> Create Quote
                </Button>
              </div>
              {isAdmin && (
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 h-8 px-2" onClick={() => setConfirmDel(l)} data-testid={`del-lead-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Lead
                  </Button>
                </div>
              )}
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">No leads match your filters.</div>
          )}
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete lead {confirmDel?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{confirmDel?.name}</strong> from the CRM. Any generated proposals will remain but will be orphaned.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeLead} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-del-lead">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
